import { useState, useRef, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import axios from "axios";
import { 
  Camera, 
  Video, 
  Image, 
  AtSign, 
  X, 
  Check,
  Upload,
  Loader2,
  Globe,
  Users,
  Building2,
  Search,
  MapPin,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Crop
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const StoryCreator = ({ open, onClose, onStoryCreated, user }) => {
  const [step, setStep] = useState(1);
  const [mediaType, setMediaType] = useState(null);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [caption, setCaption] = useState("");
  const [privacy, setPrivacy] = useState("public");
  const [venueCheckIn, setVenueCheckIn] = useState(null);
  const [mentions, setMentions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [venues, setVenues] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showVenueSearch, setShowVenueSearch] = useState(false);
  const [showMentionSearch, setShowMentionSearch] = useState(false);
  
  // Fotoğraf düzenleme state'leri
  const [imageZoom, setImageZoom] = useState(100);
  const [imageRotation, setImageRotation] = useState(0);
  const [editedImage, setEditedImage] = useState(null);
  
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const response = await axios.get(`${API}/api/venues`);
        setVenues(response.data);
      } catch (error) {
        console.error("Venues fetch error:", error);
      }
    };
    if (open) fetchVenues();
  }, [open]);

  useEffect(() => {
    const searchUsers = async () => {
      if (searchQuery.length < 2) {
        setUsers([]);
        return;
      }
      try {
        const response = await axios.get(`${API}/api/users/search?q=${searchQuery}`);
        setUsers(response.data.users || []);
      } catch (error) {
        console.error("User search error:", error);
      }
    };
    
    const debounce = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isVideo = file.type.startsWith("video/");
    const isImage = file.type.startsWith("image/");

    if (!isVideo && !isImage) {
      toast.error("Sadece resim veya video yükleyebilirsiniz");
      return;
    }

    if (isVideo) {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        if (video.duration > 15) {
          toast.error("Video en fazla 15 saniye olabilir");
          return;
        }
        setMediaType("video");
        setMediaFile(file);
        setMediaPreview(URL.createObjectURL(file));
        setStep(2);
      };
      video.src = URL.createObjectURL(file);
    } else {
      setMediaType("image");
      setMediaFile(file);
      setMediaPreview(URL.createObjectURL(file));
      setImageZoom(100);
      setImageRotation(0);
      setStep(2);
    }
  };

  // Fotoğraf düzenleme fonksiyonları
  const applyImageEdits = useCallback(() => {
    if (!mediaPreview || mediaType !== "image" || !canvasRef.current || !imageRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const img = imageRef.current;

    // Canvas boyutunu ayarla
    const size = 800;
    canvas.width = size;
    canvas.height = size;

    // Temizle
    ctx.clearRect(0, 0, size, size);
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, size, size);

    // Transform uygula
    ctx.save();
    ctx.translate(size / 2, size / 2);
    ctx.rotate((imageRotation * Math.PI) / 180);
    ctx.scale(imageZoom / 100, imageZoom / 100);

    // Resmi ortala ve çiz
    const scale = Math.max(size / img.naturalWidth, size / img.naturalHeight);
    const w = img.naturalWidth * scale;
    const h = img.naturalHeight * scale;
    ctx.drawImage(img, -w / 2, -h / 2, w, h);

    ctx.restore();

    // Data URL'e dönüştür
    setEditedImage(canvas.toDataURL("image/jpeg", 0.8));
  }, [mediaPreview, mediaType, imageZoom, imageRotation]);

  useEffect(() => {
    if (step === 2 && mediaType === "image") {
      const timer = setTimeout(applyImageEdits, 100);
      return () => clearTimeout(timer);
    }
  }, [step, mediaType, imageZoom, imageRotation, applyImageEdits]);

  const handleZoomChange = (value) => {
    setImageZoom(value[0]);
  };

  const handleRotate = () => {
    setImageRotation((prev) => (prev + 90) % 360);
  };

  const handleAddMention = (selectedUser) => {
    if (mentions.find(m => m.userId === selectedUser.id)) {
      toast.error("Bu kullanıcı zaten etiketli");
      return;
    }
    setMentions([...mentions, {
      userId: selectedUser.id,
      userName: selectedUser.name
    }]);
    setShowMentionSearch(false);
    setSearchQuery("");
  };

  const handleRemoveMention = (userId) => {
    setMentions(mentions.filter(m => m.userId !== userId));
  };

  const handleSelectVenue = (venue) => {
    setVenueCheckIn({
      venueId: venue.id,
      venueName: venue.name
    });
    setShowVenueSearch(false);
  };

  const uploadMedia = async () => {
    if (!mediaFile) return null;

    // Fotoğraf için düzenlenmiş versiyonu kullan
    if (mediaType === "image" && editedImage) {
      return editedImage;
    }

    const formData = new FormData();
    formData.append("file", mediaFile);
    formData.append("type", mediaType);

    try {
      const response = await axios.post(`${API}/api/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      return response.data.url;
    } catch (error) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(mediaFile);
      });
    }
  };

  const handlePublish = async () => {
    setLoading(true);
    try {
      const mediaUrl = await uploadMedia();
      
      const storyData = {
        mediaType,
        mediaUrl,
        caption,
        privacy,
        venueCheckIn,
        mentions
      };

      const response = await axios.post(`${API}/api/stories`, storyData);
      
      if (response.data.success) {
        toast.success("Hikaye paylaşıldı!");
        onStoryCreated?.(response.data.story);
        handleClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Hikaye paylaşılamadı");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setMediaType(null);
    setMediaFile(null);
    setMediaPreview(null);
    setEditedImage(null);
    setCaption("");
    setPrivacy("public");
    setVenueCheckIn(null);
    setMentions([]);
    setImageZoom(100);
    setImageRotation(0);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5 text-emerald-600" />
            {step === 1 ? "Hikaye Oluştur" : step === 2 ? "Düzenle" : "Detaylar"}
          </DialogTitle>
        </DialogHeader>

        {/* Step 1: Media Selection */}
        {step === 1 && (
          <div className="space-y-4 py-4" data-testid="story-creator-step1">
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => {
                  fileInputRef.current.accept = "image/*";
                  fileInputRef.current.click();
                }}
                className="flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-xl hover:border-emerald-500 hover:bg-emerald-50 transition-colors"
                data-testid="story-photo-button"
              >
                <Image className="h-12 w-12 text-emerald-600 mb-2" />
                <span className="font-medium">Fotoğraf</span>
              </button>
              <button
                onClick={() => {
                  fileInputRef.current.accept = "video/*";
                  fileInputRef.current.click();
                }}
                className="flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-xl hover:border-emerald-500 hover:bg-emerald-50 transition-colors"
                data-testid="story-video-button"
              >
                <Video className="h-12 w-12 text-emerald-600 mb-2" />
                <span className="font-medium">Video</span>
                <span className="text-xs text-gray-500">Max 15sn</span>
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileSelect}
              data-testid="story-file-input"
            />
          </div>
        )}

        {/* Step 2: Preview & Edit */}
        {step === 2 && (
          <div className="space-y-4 py-4">
            <div className="relative bg-black rounded-lg overflow-hidden aspect-[9/16] max-h-[400px]">
              {mediaType === "video" ? (
                <video
                  ref={videoRef}
                  src={mediaPreview}
                  className="w-full h-full object-contain"
                  controls
                  autoPlay
                  muted
                />
              ) : (
                <>
                  {/* Gizli image ref */}
                  <img
                    ref={imageRef}
                    src={mediaPreview}
                    alt="Original"
                    className="hidden"
                    onLoad={applyImageEdits}
                  />
                  {/* Gizli canvas */}
                  <canvas ref={canvasRef} className="hidden" />
                  {/* Düzenlenmiş görüntü */}
                  <img
                    src={editedImage || mediaPreview}
                    alt="Preview"
                    className="w-full h-full object-contain"
                  />
                </>
              )}
            </div>
            
            {/* Fotoğraf düzenleme kontrolleri */}
            {mediaType === "image" && (
              <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Crop className="h-4 w-4 text-gray-600" />
                  <span className="text-sm font-medium">Düzenleme</span>
                </div>
                
                {/* Zoom */}
                <div className="flex items-center gap-3">
                  <ZoomOut className="h-4 w-4 text-gray-500" />
                  <Slider
                    value={[imageZoom]}
                    onValueChange={handleZoomChange}
                    min={50}
                    max={200}
                    step={5}
                    className="flex-1"
                  />
                  <ZoomIn className="h-4 w-4 text-gray-500" />
                  <span className="text-xs text-gray-500 w-12">{imageZoom}%</span>
                </div>
                
                {/* Döndür */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRotate}
                  className="w-full"
                >
                  <RotateCw className="h-4 w-4 mr-2" />
                  Döndür ({imageRotation}°)
                </Button>
              </div>
            )}
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                <X className="h-4 w-4 mr-2" />
                Değiştir
              </Button>
              <Button onClick={() => setStep(3)} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                <Check className="h-4 w-4 mr-2" />
                Devam
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Details */}
        {step === 3 && (
          <div className="space-y-4 py-4">
            {/* Mini Preview */}
            <div className="relative bg-black rounded-lg overflow-hidden h-32">
              {mediaType === "video" ? (
                <video src={mediaPreview} className="w-full h-full object-cover" muted />
              ) : (
                <img src={editedImage || mediaPreview} alt="Preview" className="w-full h-full object-cover" />
              )}
            </div>

            {/* Caption */}
            <div className="space-y-2">
              <Label>Açıklama</Label>
              <Textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Bir şeyler yaz..."
                rows={2}
                maxLength={200}
              />
              <p className="text-xs text-gray-500 text-right">{caption.length}/200</p>
            </div>

            {/* Privacy */}
            <div className="space-y-2">
              <Label>Gizlilik</Label>
              <Select value={privacy} onValueChange={setPrivacy} data-testid="story-privacy-select">
                <SelectTrigger data-testid="story-privacy-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public" data-testid="story-privacy-public">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      Herkes
                    </div>
                  </SelectItem>
                  <SelectItem value="connections" data-testid="story-privacy-connections">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Sadece Bağlantılar
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Venue Check-in */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Halı Saha Check-in
              </Label>
              {venueCheckIn ? (
                <div className="flex items-center justify-between bg-emerald-50 p-2 rounded-lg">
                  <span className="text-emerald-700 font-medium">{venueCheckIn.venueName}</span>
                  <Button variant="ghost" size="sm" onClick={() => setVenueCheckIn(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => setShowVenueSearch(true)}
                >
                  <MapPin className="h-4 w-4 mr-2" />
                  Saha Seç
                </Button>
              )}
              
              {showVenueSearch && (
                <div className="border rounded-lg p-2 max-h-40 overflow-y-auto">
                  {venues.slice(0, 10).map(venue => (
                    <button
                      key={venue.id}
                      onClick={() => handleSelectVenue(venue)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded"
                    >
                      {venue.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Mentions */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <AtSign className="h-4 w-4" />
                Kişi Etiketle
              </Label>
              
              {mentions.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {mentions.map(mention => (
                    <Badge key={mention.userId} variant="secondary" className="flex items-center gap-1">
                      @{mention.userName}
                      <button onClick={() => handleRemoveMention(mention.userId)}>
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => setShowMentionSearch(!showMentionSearch)}
              >
                <AtSign className="h-4 w-4 mr-2" />
                Kişi Ekle
              </Button>
              
              {showMentionSearch && (
                <div className="border rounded-lg p-2">
                  <div className="relative mb-2">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Kullanıcı ara..."
                      className="pl-8"
                    />
                  </div>
                  <div className="max-h-32 overflow-y-auto">
                    {users.map(u => (
                      <button
                        key={u.id}
                        onClick={() => handleAddMention(u)}
                        className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-gray-100 rounded"
                      >
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={u.profileImage} />
                          <AvatarFallback>{u.name?.[0]}</AvatarFallback>
                        </Avatar>
                        <span className="text-sm">{u.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {step === 3 && (
          <DialogFooter>
            <Button variant="outline" onClick={() => setStep(2)}>
              Geri
            </Button>
            <Button
              onClick={handlePublish}
              disabled={loading}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Paylaşılıyor...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Paylaş
                </>
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StoryCreator;
